/**
 * Platform Admin App Component
 * 
 * Handles routing for platform admin subdomain (admin.eduna.com)
 * Requires platform admin authentication
 */

import React, { Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { CContainer } from '@coreui/react'

import { checkAuthStatus } from '../store/slices/authSlice'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorBoundary from '../components/common/ErrorBoundary'
import PlatformAdminSidebar from '../components/layout/PlatformAdminSidebar'
import PlatformAdminHeader from '../components/layout/PlatformAdminHeader'

// Lazy load components
const Login = React.lazy(() => import('../views/auth/Login'))
const PlatformAdminDashboard = React.lazy(() => import('../views/platform-admin/PlatformAdminDashboard'))
const PendingTenants = React.lazy(() => import('../views/platform-admin/PendingTenants'))
const JobMonitorDashboard = React.lazy(() => import('../views/platform-admin/JobMonitorDashboard'))
const SubscriptionPlansManagement = React.lazy(() => import('../views/platform-admin/SubscriptionPlansManagement'))
const TenantBillingDashboard = React.lazy(() => import('../views/platform-admin/TenantBillingDashboard'))
const PlatformRevenueReports = React.lazy(() => import('../views/platform-admin/PlatformRevenueReports'))
const PaymentProvidersManagement = React.lazy(() => import('../views/platform-admin/PaymentProvidersManagement'))
const RBACManagement = React.lazy(() => import('../views/platform-admin/RBACManagement'))
const SubscriptionRequests = React.lazy(() => import('../views/platform-admin/SubscriptionRequests'))
const ContactSubmissions = React.lazy(() => import('../views/platform-admin/ContactSubmissions'))

function PlatformAdminApp() {
  const dispatch = useDispatch()
  const { isAuthenticated, user, token } = useSelector((state) => state.auth)
  const [isRehydrated, setIsRehydrated] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)

  useEffect(() => {
    // Wait for redux-persist to rehydrate
    const timer = setTimeout(() => {
      setIsRehydrated(true)
    }, 100)
    
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Check auth status after rehydration
    if (isRehydrated && !authChecked) {
      if (token) {
        dispatch(checkAuthStatus()).finally(() => {
          setAuthChecked(true)
        })
      } else {
        setAuthChecked(true)
      }
    }
  }, [isRehydrated, authChecked, token, dispatch])

  // Show loading while rehydrating or checking auth
  if (!isRehydrated || !authChecked) {
    return <LoadingSpinner />
  }

  // If authenticated but user object not loaded yet, show loading
  if (isAuthenticated && !user) {
    return <LoadingSpinner />
  }

  const isPlatformAdmin = user?.is_platform_admin || user?.isPlatformAdmin

  return (
    <ErrorBoundary>
      <Helmet>
        <title>EDUNA Platform Administration</title>
        <meta name="description" content="Platform administration for EDUNA" />
      </Helmet>
      
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Login route */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected platform admin routes with layout */}
          {isAuthenticated && isPlatformAdmin ? (
            <Route path="/*" element={
              <div className="custom-admin-bg">
                <PlatformAdminSidebar />
                <div className="wrapper d-flex flex-column min-vh-100 custom-admin-bg">
                  <PlatformAdminHeader />
                  <div className="body flex-grow-1 px-3">
                    <CContainer lg>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<PlatformAdminDashboard />} />
                        <Route path="/tenants/pending" element={<PendingTenants />} />
                        <Route path="/tenants" element={<PlatformAdminDashboard />} />
                        <Route path="/tenants/verified" element={<PlatformAdminDashboard />} />
                        <Route path="/tenants/suspended" element={<PlatformAdminDashboard />} />
                        <Route path="/payment-providers" element={<PaymentProvidersManagement />} />
                        <Route path="/subscriptions/plans" element={<SubscriptionPlansManagement />} />
                        <Route path="/subscriptions/requests" element={<SubscriptionRequests />} />
                        <Route path="/billing" element={<TenantBillingDashboard />} />
                        <Route path="/reports/revenue" element={<PlatformRevenueReports />} />
                        <Route path="/rbac" element={<RBACManagement />} />
                        <Route path="/analytics" element={<PlatformAdminDashboard />} />
                        <Route path="/users/stats" element={<PlatformAdminDashboard />} />
                        <Route path="/jobs" element={<JobMonitorDashboard />} />
                        <Route path="/contacts" element={<ContactSubmissions />} />
                        <Route path="/settings" element={<PlatformAdminDashboard />} />
                        <Route path="/security" element={<PlatformAdminDashboard />} />
                      </Routes>
                    </CContainer>
                  </div>
                </div>
              </div>
            } />
          ) : (
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

export default PlatformAdminApp
